import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  region: "New Hampshire",
  preferred_date: "",
  concern: "Parkinson's",
  message: "",
  consent_privacy: false,
  consent_telehealth: false,
};

const REGION_MAP = { NH: "New Hampshire", VT: "Vermont", IE: "Ireland" };

export const AppointmentDialog = ({ open, onOpenChange, initialRegion }) => {
  const startRegion = REGION_MAP[initialRegion] || "New Hampshire";
  const [form, setForm] = useState({ ...initial, region: startRegion });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent_privacy || !form.consent_telehealth) {
      setError("Both consent boxes are required to submit a request.");
      return;
    }
    if (form.message.trim().length < 10) {
      setError("Please add a brief note (10+ characters).");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, form);
      setDone(true);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please email neurocompartners@gmail.com.";
      setError(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(initial);
    setDone(false);
    setError("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent
        data-testid="appointment-dialog"
        className="max-w-2xl bg-[#FDFCF8] border border-black/5 rounded-3xl p-0 overflow-hidden"
      >
        <div className="p-8 md:p-10 max-h-[85vh] overflow-y-auto">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-8"
              data-testid="appointment-success"
            >
              <CheckCircle2
                size={48}
                strokeWidth={1.3}
                className="text-[#739072] mx-auto mb-6"
              />
              <h3 className="font-serif text-3xl text-[#2D332F] mb-3">
                Request received.
              </h3>
              <p className="text-[#5C6661] max-w-md mx-auto leading-relaxed">
                Kathleen will review your request and reply by email within 1–2
                business days. You will receive a secure Zoom for Healthcare
                link before your session.
              </p>
              <button
                onClick={() => onOpenChange(false)}
                data-testid="appointment-close"
                className="mt-8 px-6 py-3 rounded-full bg-[#2D332F] text-[#FDFCF8] text-sm hover:bg-[#739072] transition-colors"
              >
                Close
              </button>
            </motion.div>
          ) : (
            <>
              <DialogHeader className="text-left mb-6">
                <div className="mb-4">
                  <img
                    src="/logo.jpg"
                    alt="Neurological Communication Partners"
                    className="h-20 w-auto rounded-xl ring-1 ring-black/5"
                  />
                </div>
                <p className="text-xs uppercase tracking-[0.22em] text-[#5C6661] mb-2">
                  Request an appointment
                </p>
                <DialogTitle className="font-serif text-3xl md:text-4xl font-light tracking-tight text-[#2D332F]">
                  Let&rsquo;s start the conversation.
                </DialogTitle>
                <DialogDescription className="text-[#5C6661]">
                  Kathleen will be in touch to confirm scheduling and send a
                  secure Zoom for Healthcare link.
                </DialogDescription>
              </DialogHeader>

              <form
                onSubmit={handleSubmit}
                className="space-y-5"
                data-testid="appointment-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ap-name" className="text-[#2D332F]">
                      Full name
                    </Label>
                    <Input
                      id="ap-name"
                      data-testid="appointment-name"
                      value={form.name}
                      onChange={(e) => set("name")(e.target.value)}
                      required
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ap-email" className="text-[#2D332F]">
                      Email
                    </Label>
                    <Input
                      id="ap-email"
                      data-testid="appointment-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email")(e.target.value)}
                      required
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ap-phone" className="text-[#2D332F]">
                      Phone (optional)
                    </Label>
                    <Input
                      id="ap-phone"
                      data-testid="appointment-phone"
                      value={form.phone}
                      onChange={(e) => set("phone")(e.target.value)}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-[#2D332F]">Region</Label>
                    <Select
                      value={form.region}
                      onValueChange={(v) => set("region")(v)}
                    >
                      <SelectTrigger
                        data-testid="appointment-region"
                        className="mt-1.5"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="New Hampshire">New Hampshire</SelectItem>
                        <SelectItem value="Vermont">Vermont</SelectItem>
                        <SelectItem value="Ireland">Ireland</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#2D332F]">Primary concern</Label>
                    <Select
                      value={form.concern}
                      onValueChange={(v) => set("concern")(v)}
                    >
                      <SelectTrigger
                        data-testid="appointment-concern"
                        className="mt-1.5"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Parkinson's">
                          Parkinson&rsquo;s disease
                        </SelectItem>
                        <SelectItem value="Cognitive">
                          Cognitive change
                        </SelectItem>
                        <SelectItem value="Post-CVA">
                          Post-stroke (CVA)
                        </SelectItem>
                        <SelectItem value="Swallow">
                          Swallow / Dysphagia
                        </SelectItem>
                        <SelectItem value="Voice">Voice</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="ap-date" className="text-[#2D332F]">
                      Preferred date / time (optional)
                    </Label>
                    <Input
                      id="ap-date"
                      data-testid="appointment-date"
                      placeholder="e.g. weekday mornings"
                      value={form.preferred_date}
                      onChange={(e) => set("preferred_date")(e.target.value)}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="ap-msg" className="text-[#2D332F]">
                    Brief note
                  </Label>
                  <Textarea
                    id="ap-msg"
                    data-testid="appointment-message"
                    rows={4}
                    value={form.message}
                    onChange={(e) => set("message")(e.target.value)}
                    placeholder="A few sentences about what you'd like help with."
                    className="mt-1.5"
                    required
                  />
                </div>

                <div className="rounded-2xl border border-black/5 bg-[#F3F1E7] p-4 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={form.consent_privacy}
                      onCheckedChange={(v) => set("consent_privacy")(!!v)}
                      data-testid="appointment-consent-privacy"
                      className="mt-0.5"
                    />
                    <span className="text-sm text-[#2D332F] leading-relaxed">
                      I have read the{" "}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline text-[#739072]"
                      >
                        Privacy Notice
                      </a>{" "}
                      and consent to my information being processed under HIPAA
                      (USA) &amp; GDPR (EU/Ireland) for the purpose of
                      scheduling.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={form.consent_telehealth}
                      onCheckedChange={(v) => set("consent_telehealth")(!!v)}
                      data-testid="appointment-consent-telehealth"
                      className="mt-0.5"
                    />
                    <span className="text-sm text-[#2D332F] leading-relaxed">
                      I understand sessions are conducted via{" "}
                      <strong>Zoom for Healthcare</strong> (BAA-covered) and
                      consent to telehealth services with Kathleen McGrath, M.S.
                      CCC-SLP.
                    </span>
                  </label>
                </div>

                {error && (
                  <p
                    role="alert"
                    data-testid="appointment-error"
                    className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="appointment-submit"
                  className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#2D332F] text-[#FDFCF8] text-sm tracking-wide hover:bg-[#739072] transition-all duration-300 disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      Send request
                      <ArrowUpRight size={16} strokeWidth={1.5} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
